// Single source of the controlled vocabulary for the submission form AND the
// server-side validator (netlify/functions/submit-organization.mjs). Pure data
// — no React/DOM — so the Netlify function can import it directly. Keeping the
// vocab here means the form and validator can never drift (submission contract).
//
// IMPORTANT: every value here must exactly match an existing option in the
// corresponding Baserow single/multi-select field. Reconcile against Baserow
// when wiring the live write path — Baserow rejects unknown option values.

import { ACTIVITY_TYPOLOGY, THEME_TYPOLOGY } from './taxonomies.js';
import {
  SCALE_LEVELS, POLICY_LEVELS, IMPLEMENTATION_LEVELS, POWER_LEVELS,
  STAKEHOLDER_TYPES, ORG_TYPES,
} from './lenses.js';

// Activity/Theme leaf values: "Parent | Subcategory" for each sub, plus a
// "Parent [Other]" catch-all per parent (both forms appear in the data).
const leaves = (typology) =>
  typology.flatMap((c) => [
    ...c.sub.map((s) => `${c.name} | ${s}`),
    `${c.name} [Other]`,
  ]);

// Canonical country single-select vocabulary (from the map's EU_HEX keys).
// Deliberately excludes dirty directory values like "Europe" and
// "Netherlands;Spain" that exist in legacy data.
export const COUNTRIES = [
  'Albania', 'Austria', 'Belgium', 'Bosnia & Herzegovina', 'Bulgaria',
  'Croatia', 'Cyprus', 'Czechia', 'Denmark', 'Estonia', 'Finland', 'France',
  'Germany', 'Greece', 'Hungary', 'Iceland', 'Ireland', 'Italy', 'Kosovo',
  'Latvia', 'Lithuania', 'Luxembourg', 'Malta', 'Moldova', 'Montenegro',
  'Netherlands', 'North Macedonia', 'Norway', 'Poland', 'Portugal', 'Romania',
  'Serbia', 'Slovakia', 'Slovenia', 'Spain', 'Sweden', 'Switzerland',
  'Turkey', 'Ukraine',
  'United Kingdom - England', 'United Kingdom - Northern Ireland',
  'United Kingdom - Scotland', 'United Kingdom - Wales',
];

export const ACTIVITY_VALUES = leaves(ACTIVITY_TYPOLOGY);
export const THEME_VALUES = leaves(THEME_TYPOLOGY);
export const TYPE_VALUES = [...ORG_TYPES];
export const SCALE_VALUES = SCALE_LEVELS.map((s) => s.value);
export const POLICY_VALUES = POLICY_LEVELS.map((s) => s.value);
export const IMPLEMENTATION_VALUES = IMPLEMENTATION_LEVELS.map((s) => s.value);
export const POWER_VALUES = POWER_LEVELS.map((s) => s.value);
export const STAKEHOLDER_VALUES = [...STAKEHOLDER_TYPES];

// Allowed values per orgs.json field key — the validator's lookup table.
// `country` is single-select (one value); the rest are multi-select (arrays).
export const VOCAB = {
  country: COUNTRIES,
  type: TYPE_VALUES,
  activities: ACTIVITY_VALUES,
  themes: THEME_VALUES,
  scale: SCALE_VALUES,
  stakeholders: STAKEHOLDER_VALUES,
  policy: POLICY_VALUES,
  implementation: IMPLEMENTATION_VALUES,
  power: POWER_VALUES,
};

// Which orgs.json keys are multi-select (arrays) vs single-value.
export const MULTI_SELECT_FIELDS = [
  'type', 'activities', 'themes', 'scale', 'stakeholders',
  'policy', 'implementation', 'power',
];
export const SINGLE_SELECT_FIELDS = ['country'];

// Fast membership lookup for the validator.
export const VOCAB_SETS = Object.fromEntries(
  Object.entries(VOCAB).map(([k, vals]) => [k, new Set(vals)]),
);
